import { NavLink } from 'react-router-dom';
import { Store, LogOut, Bell, ChevronDown, ChevronRight, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import LocalServerBadge from './LocalServerBadge';
import LocalServerUpdateButton from '../common/LocalServerUpdateButton';
import LicenseExpiryBanner from '../common/LicenseExpiryBanner';
import LocalServerDownloadMenu from '../common/LocalServerDownloadMenu';
import MobileApkDownloadButton from '../common/MobileApkDownloadButton';

export default function POSNavbar() {
    const { user, logout } = useAuth();

    const prefix = '/pos';
    const configPrefix = '/pos/config';
    const MANAGEMENT_ROLES = ['super_admin', 'admin', 'manager', 'partner'];

    const navItems = [
        { name: 'Tableau de bord', href: '/pos/dashboard' },
        {
            name: '🏠 Établissement',
            href: '#',
            children: [
                { name: 'Détails du restaurant', href: `${configPrefix}/details` },
                { name: "Horaires d'ouverture", href: `${configPrefix}/hours` },
                { name: 'Utilisateurs & Droits', href: `${configPrefix}/users` },
                { name: "Accès & Rôles POS", href: `${configPrefix}/access-rights` },
                { name: 'Documents comptables', href: `${configPrefix}/comptable` },
                { name: 'Fiscalité (NACEF)', href: '/pos/settings/fiscal' },
                { name: "Générer QR code avis client", href: `${configPrefix}/customer-review-qr` }
            ]
        },
        {
            name: '💳 Ventes & Caisses',
            href: '#',
            children: [
                { name: 'Gérer les Caisses (POS)', href: `/pos/manage`, roles: MANAGEMENT_ROLES },
                { name: 'Modes de règlement', href: `${configPrefix}/payment-methods` },
                { name: 'Taxes & TVA', href: `${configPrefix}/taxes` },
                { name: 'Matériel (Imprimantes)', href: `${prefix}/printers` },
                { name: 'Écrans d\'affichage', href: `${prefix}/displays` },
                { name: 'Cuisine (KDS)', href: '/pos/kds' },
                { name: 'Dettes & Crédits Clients', href: `${prefix}/debts` },
                { name: 'Réservations de tables', href: `${prefix}/reservations` }
            ]
        },
        {
            name: '📦 Catalogue & Stocks',
            href: '#',
            children: [
                { name: 'Catégories', href: `${prefix}/categories` },
                { name: 'Produits & Menus', href: `${prefix}/products` },
                { name: 'Préparation Cuisine', href: `${prefix}/production` },
                { name: 'Plan de Salle', href: `${prefix}/floor-plan` },
                { name: 'Gestion des stocks', href: `${prefix}/inventory` },
                { name: 'Dépôts (Entrepôts)', href: `${prefix}/inventory/warehouses` },
                { name: 'Inventaires', href: `${prefix}/inventory/counts` },
                { name: 'Alertes de Stock', href: `${prefix}/purchases/alerts` },
                { name: 'Transferts de stock', href: `${prefix}/purchases/transfers` }
            ]
        },
        {
            name: '🛒 Achats & Fournisseurs',
            href: '#',
            children: [
                { name: 'Fournisseurs', href: `${prefix}/purchases/suppliers` },
                { name: 'Commandes d\'Achat', href: `${prefix}/purchases/orders` },
                { name: 'Bons de Réception', href: `${prefix}/purchases/receptions` },
                { name: 'Factures Achats', href: `${prefix}/purchases/invoices` },
                { name: 'Paiements', href: `${prefix}/purchases/payments`, roles: MANAGEMENT_ROLES }
            ]
        },
        {
            name: '📊 Rapports',
            href: '#',
            children: [
                { name: 'Vue d\'ensemble', href: `${prefix}/analysis` },
                { name: 'Ventes & Recettes', href: `${prefix}/analysis/sales` },
                { name: 'Historique des Sessions', href: `/pos/sessions` },
                { name: 'Écarts de Caisse', href: `${prefix}/analysis/cash-discrepancies`, roles: MANAGEMENT_ROLES },
                { name: 'Activité Caisse', href: `${prefix}/analysis/cash-activity`, roles: MANAGEMENT_ROLES }
            ]
        }
    ];

    return (
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
                <div className="flex justify-between h-16">
                    {/* Left Side: Brand & Nav */}
                    <div className="flex min-w-0 flex-1">
                        <div className="flex-shrink-0 flex items-center gap-2 mr-2 lg:mr-4 xl:mr-8">
                            <div className="bg-orange-600 p-1.5 rounded-lg text-white">
                                <Store size={20} />
                            </div>
                            <span className="hidden xl:inline font-bold text-gray-900 text-lg">Point de Vente</span>
                        </div>

                        <div className="hidden md:flex md:items-center gap-0.5 lg:gap-1 xl:gap-4 min-w-0 flex-1">
                            {navItems.map((item) => (
                                <div key={item.name} className="relative group flex items-center h-full shrink-0">
                                    {item.children ? (
                                        <>
                                            <button className="inline-flex items-center px-1.5 lg:px-2 xl:px-3 pt-1 border-b-2 border-transparent text-xs lg:text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors duration-200 h-full whitespace-nowrap">
                                                {item.name}
                                                <ChevronDown size={14} className="ml-1" />
                                            </button>

                                            {/* Dropdown Menu */}
                                            <div className="absolute top-16 left-0 w-64 bg-white shadow-lg ring-1 ring-black ring-opacity-5 rounded-b-md p-1 invisible group-hover:visible transition-all duration-200 transform origin-top-left z-50">
                                                {item.children.filter((child: any) => (!child.roles || child.roles.includes(user?.role)) && (!child.requiredFeature || user?.plan_features?.includes(child.requiredFeature))).map((child) => (
                                                    (child as any).children ? (
                                                        <div key={child.name} className="relative group/nested w-full">
                                                            <button className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                                                                {child.name}
                                                                <ChevronRight size={14} className="text-gray-400" />
                                                            </button>
                                                            {/* Nested Dropdown */}
                                                            <div className="absolute top-0 left-full w-56 bg-white shadow-lg ring-1 ring-black ring-opacity-5 rounded-md p-1 invisible group-hover/nested:visible transition-all duration-200 ml-1">
                                                                {(child as any).children.map((subChild: any) => (
                                                                    <NavLink
                                                                        key={subChild.name}
                                                                        to={subChild.href}
                                                                        className={({ isActive }: { isActive: boolean }) =>
                                                                            `block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md ${isActive ? 'bg-orange-50 text-orange-700' : ''}`
                                                                        }
                                                                    >
                                                                        {subChild.name}
                                                                    </NavLink>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <NavLink
                                                            key={child.name}
                                                            to={child.href}
                                                            className={({ isActive }: { isActive: boolean }) =>
                                                                `block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md ${isActive ? 'bg-orange-50 text-orange-700' : ''}`
                                                            }
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    )
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <NavLink
                                            to={item.href}
                                            target={item.name === 'Cuisine (KDS)' ? '_blank' : undefined}
                                            className={({ isActive }: { isActive: boolean }) =>
                                                `inline-flex items-center px-1.5 lg:px-2 xl:px-3 pt-1 border-b-2 text-xs lg:text-sm font-medium transition-colors duration-200 h-full whitespace-nowrap
                                                ${isActive
                                                    ? 'border-orange-500 text-gray-900'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`
                                            }
                                        >
                                            {item.name}
                                        </NavLink>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Side: Search & Profile */}
                    <div className="flex items-center gap-4 shrink-0">

                        <div className="flex items-center gap-3">
                            <LicenseExpiryBanner />
                            <LocalServerBadge />
                            <LocalServerUpdateButton />
                            <LocalServerDownloadMenu />
                            <MobileApkDownloadButton />

                            {/* Switch POS */}
                            <NavLink
                                to="/pos/select"
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Changer de caisse"
                            >
                                <ArrowRightLeft size={20} />
                            </NavLink>

                            {/* Notifications */}
                            <button className="p-1 text-gray-400 hover:text-gray-600 relative">
                                <Bell size={18} />
                                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
                            </button>

                            {/* User Profile Dropdown Trigger (Simplified) */}
                            <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded-md transition-colors">
                                <span className="hidden sm:block text-sm font-medium text-gray-700">
                                    {user?.first_name || 'Gérant'}
                                </span>
                                <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold border border-indigo-200">
                                    {(user?.first_name?.[0] || 'U')}
                                </div>
                            </div>

                            {/* Logout */}
                            <button
                                onClick={logout}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                title="Déconnexion"
                            >
                                <LogOut size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}
